// Voice, on screen (ui/dictation.ts does the work): the pill over the terminal while someone
// talks, the Voice buttons (pane header, phone key bar), and the keys (Settings › Voice: hold
// one to talk, press another for hands-free). Tap a button for hands-free, hold it for
// push-to-talk.

import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type PointerEvent as RPointerEvent,
  type ReactNode,
} from "react";
import { IconMicrophoneOff } from "@tabler/icons-react";
import { store } from "../store";
import {
  cancelVoice,
  finishVoice,
  lockVoice,
  releaseVoice,
  startVoice,
  useVoice,
  voiceLevel,
  voiceState,
  setVoiceCancelling,
} from "./dictation";
import { Ic } from "./ds";
import {
  bindingLabel,
  DOUBLE_TAP_MS,
  held,
  HOLD_ARM_MS,
  matches,
  releases,
  type Binding,
  type Mod,
  type Shortcut,
} from "./voiceGesture";
import { onVoiceShortcuts, recordingKeys, voiceBindings } from "./voiceKeys";
import { isMac } from "./keys";
import { MAX_SECONDS } from "./voice";
import { useMedia } from "./mobile";

/** The key that holds to talk, as the person's keyboard names it ("⌥ Space"): the first one chosen. */
export const voiceKeyLabel = () => bindingLabel(voiceBindings().push[0], isMac());
/** …and the one that talks hands-free ("⌥⇧ Space"). */
export const voiceFreeKeyLabel = () => bindingLabel(voiceBindings().handsFree[0], isMac());

const MOD_KEYS: readonly string[] = ["Control", "Alt", "Meta", "Shift"];
/** The modifiers down besides `except` (a held modifier by itself means no others). */
const othersDown = (e: KeyboardEvent, except: string) =>
  (except !== "Control" && e.ctrlKey) || (except !== "Alt" && e.altKey) || (except !== "Meta" && e.metaKey) || (except !== "Shift" && e.shiftKey);
const chordIn = (l: Binding[], e: KeyboardEvent): Shortcut | null => {
  for (const b of l) if (b.kind === "chord" && matches(e, b)) return b;
  return null;
};
const modIn = (l: Binding[], kind: "hold" | "double", mod: string) => l.some((b) => b.kind === kind && b.mod === (mod as Mod));
/** The keys' labels, re-read when Settings › Voice changes them. */
export const useVoiceKeyLabel = () =>
  useSyncExternalStore(onVoiceShortcuts, voiceKeyLabel);

const REDUCED_MQ = "(prefers-reduced-motion: reduce)";

/** Typing somewhere other than a terminal (a rename field, the palette): the voice keys are theirs. */
function typingElsewhere() {
  const el = document.activeElement;
  if (
    !(el instanceof HTMLElement) ||
    el.classList.contains("xterm-helper-textarea")
  )
    return false;
  return (
    el.isContentEditable ||
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  );
}

/**
 * The voice keys in the focused session (Settings › Voice; ⌥ Space and ⌥⇧ Space until chosen
 * otherwise): hold the first to talk and let go to insert, or press the second to talk
 * hands-free (Return, or the same key again, inserts). Esc cancels either way. While a key is
 * held, keys don't reach the terminal, nor do the repeats of one still down after the
 * modifier was let go.
 */
export function useVoiceKeys() {
  useEffect(() => {
    /** What holds the talking: a chord (let go of its key or a modifier) or a modifier by itself. */
    let held: Shortcut | Mod | null = null;
    /** The key (code) whose press we took: its repeats and its release aren't the terminal's either. */
    let taken: string | null = null;
    /** A modifier down by itself so far (no other key since): it may become a hold or a tap. */
    let lone: { mod: string; at: number } | null = null;
    let arm: number | undefined;
    /** The last clean tap of a modifier, for a double tap. */
    let lastTap: { mod: string; at: number } | null = null;
    const swallow = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const disarm = () => {
      window.clearTimeout(arm);
      arm = undefined;
    };
    const onDown = (e: KeyboardEvent) => {
      if (recordingKeys()) return; // Settings › Voice is recording a shortcut: every key is its
      if (e.key === "Escape" && voiceState().phase !== "idle") {
        swallow(e);
        held = null;
        cancelVoice();
        return;
      }
      const keys = voiceBindings();
      const isMod = MOD_KEYS.includes(e.key);
      if (!isMod) {
        lone = null;
        lastTap = null;
        disarm();
      }
      const free = !e.repeat && !isMod ? chordIn(keys.handsFree, e) : null;
      // Hands-free: Return, or either voice chord again, inserts (Return never reaches the terminal then).
      const v = voiceState();
      if (v.handsFree && v.phase === "recording" && !held && !isMod && (e.key === "Enter" || free || (!e.repeat && chordIn(keys.push, e)))) {
        swallow(e);
        if (e.key !== "Enter") taken = e.code;
        void finishVoice();
        return;
      }
      // Holding a modifier to talk and a hands-free chord comes: hands-free from here (as fn then Space on the Mac).
      if (typeof held === "string" && free) {
        swallow(e);
        held = null;
        taken = e.code;
        lockVoice();
        return;
      }
      if (held || (taken && e.code === taken)) return swallow(e);
      if (typingElsewhere()) return;
      if (isMod) {
        if (e.repeat) return;
        lone = othersDown(e, e.key) ? null : { mod: e.key, at: e.timeStamp };
        disarm();
        if (lone && modIn(keys.push, "hold", e.key)) {
          const mod = e.key as Mod;
          // only once it has been down a while with nothing else: a quick Ctrl C never starts it
          arm = window.setTimeout(() => {
            arm = undefined;
            if (lone?.mod === mod && startVoice(store.get().focusedSession)) held = mod;
          }, HOLD_ARM_MS);
        }
        return;
      }
      if (free) {
        swallow(e); // also while the last clip is still being transcribed: not a stray key
        taken = e.code;
        if (startVoice(store.get().focusedSession, e.timeStamp)) lockVoice();
        return;
      }
      const push = chordIn(keys.push, e);
      if (!push) return;
      swallow(e);
      if (!e.repeat && startVoice(store.get().focusedSession, e.timeStamp)) held = push;
      taken = e.code;
    };
    const onUp = (e: KeyboardEvent) => {
      if (recordingKeys()) return;
      if (taken && e.code === taken) {
        taken = null;
        swallow(e);
      }
      if (typeof held === "string") {
        if (e.key === held) {
          held = null;
          lone = null;
          swallow(e);
          void finishVoice(); // held well past a tap already (it armed): it inserts
        }
        return;
      }
      if (held && releases(e, held)) {
        held = null;
        swallow(e);
        releaseVoice(e.timeStamp); // a tap goes on hands-free, a hold inserts
        return;
      }
      // A modifier let go with nothing pressed in between: a tap, maybe the second of a double tap.
      if (lone && lone.mod === e.key) {
        disarm();
        const tap = { mod: e.key, at: e.timeStamp };
        lone = null;
        const keys = voiceBindings();
        if (!modIn(keys.handsFree, "double", e.key)) return;
        if (lastTap && lastTap.mod === tap.mod && tap.at - lastTap.at < DOUBLE_TAP_MS) {
          lastTap = null;
          const v = voiceState();
          if (v.handsFree && v.phase === "recording") void finishVoice();
          else if (!typingElsewhere() && startVoice(store.get().focusedSession, e.timeStamp)) lockVoice();
        } else lastTap = tap;
      }
    };
    // The key-up never comes when the window loses focus mid-hold.
    const onBlur = () => {
      taken = null;
      lone = null;
      lastTap = null;
      disarm();
      if (held) {
        held = null;
        cancelVoice();
      }
    };
    window.addEventListener("keydown", onDown, true);
    window.addEventListener("keyup", onUp, true);
    window.addEventListener("blur", onBlur);
    return () => {
      disarm();
      window.removeEventListener("keydown", onDown, true);
      window.removeEventListener("keyup", onUp, true);
      window.removeEventListener("blur", onBlur);
    };
  }, []);
}

/**
 * Talk into session `sid`: tap for hands-free (tap again to insert), or press and hold, then
 * let go to insert; slide up to go on hands-free, slide off sideways to cancel. Focus stays
 * where it is (the terminal, and on phones its keyboard).
 */
export function VoiceButton({
  sid,
  className,
  testid,
  children,
  extra,
}: {
  sid: number;
  className: string;
  testid: string;
  children: ReactNode;
  /** more attributes for the button (the pane header's ds-btn data-*); with them it has a tooltip, so no title */
  extra?: Record<string, string>;
}) {
  const holding = useRef<number | null>(null); // the pointer holding it
  const from = useRef({ x: 0, y: 0 }); // where it went down
  const finishing = useRef<number | null>(null); // the pointer tapping it to insert (hands-free)
  const v = useVoice();
  const keyLabel = useVoiceKeyLabel();
  const live =
    v.sid === sid && (v.phase === "starting" || v.phase === "recording");
  const end = (e: RPointerEvent, insert: boolean) => {
    if (finishing.current === e.pointerId) {
      finishing.current = null;
      if (insert) void finishVoice();
      return;
    }
    if (holding.current !== e.pointerId) return;
    holding.current = null;
    if (insert) releaseVoice(e.timeStamp, e.clientX - from.current.x, e.clientY - from.current.y);
    else cancelVoice();
  };
  return (
    <button
      type="button"
      tabIndex={-1}
      {...extra}
      className={`${className} ${live ? "is-live" : ""}`}
      aria-label={
        live && v.handsFree
          ? "Insert what you said"
          : `Voice: tap to talk, or hold (${keyLabel})`
      }
      title={
        extra ? undefined : live && v.handsFree
          ? "Insert"
          : `Tap to talk, or hold (${keyLabel})`
      }
      data-testid={testid}
      data-live={live ? "1" : "0"}
      onPointerDown={(e) => {
        e.preventDefault(); // keep focus (and the on-screen keyboard) on the terminal
        if (
          e.button !== 0 ||
          holding.current !== null ||
          finishing.current !== null
        )
          return;
        const now = voiceState();
        if (now.sid === sid && now.handsFree && now.phase === "recording") {
          finishing.current = e.pointerId; // hands-free: this tap inserts
          return;
        }
        if (!startVoice(sid, e.timeStamp)) return;
        holding.current = e.pointerId;
        from.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (holding.current !== e.pointerId) return;
        // The shared rule (voiceGesture.ts): up to the lock goes on hands-free (the finger can
        // go); far enough left cancels, on release (the card says so meanwhile).
        const h = held(e.clientX - from.current.x, e.clientY - from.current.y);
        if (h.lock) {
          holding.current = null;
          lockVoice();
          return;
        }
        setVoiceCancelling(h.cancelling);
      }}
      onPointerUp={(e) => end(e, true)}
      onPointerCancel={(e) => end(e, false)}
      onMouseDown={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {children}
    </button>
  );
}


const WAVE_STEP_MS = 50; // 20 samples a second
const WAVE_KEEP = 64;

/**
 * The level as a wave in the accent, scrolling right to left: 20 samples a second, each a 2px bar
 * mirrored about the middle (quick to rise, slower to fall). Silence is five short bars breathing;
 * writing it down freezes it at 40%; with Reduce Motion it's one still bar as long as the level.
 */
function Wave({ live, writing, still }: { live: boolean; writing: boolean; still: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const hist = useRef<number[]>([]);
  const env = useRef(0);
  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const color = getComputedStyle(c).color;
    let last = performance.now();
    let raf = 0;
    const bar = (x: number, w: number, h: number) => {
      ctx.beginPath();
      ctx.roundRect(x, (c.height - h) / 2, w, h, w / 2);
      ctx.fill();
    };
    const draw = (now: number) => {
      const W = c.width;
      if (!W) return;
      const bw = 2 * dpr;
      const pitch = 4 * dpr;
      ctx.clearRect(0, 0, W, c.height);
      ctx.fillStyle = color;
      ctx.globalAlpha = writing ? 0.4 : 1;
      const rest = (alpha: number) => {
        if (alpha <= 0.01) return;
        const t = live && !still ? now / 1000 : 0;
        const xs = (W - (5 * pitch - 2 * dpr)) / 2;
        ctx.save();
        ctx.globalAlpha *= alpha;
        for (let i = 0; i < 5; i++) bar(xs + i * pitch, bw, (4 + 2 * Math.sin(t * 1.6 - i * 0.7)) * dpr);
        ctx.restore();
      };
      if (still) {
        const len = writing ? 0 : env.current * W;
        if (len > 5 * pitch) bar((W - len) / 2, len, 3 * dpr);
        else rest(1);
        return;
      }
      const h = hist.current;
      const n = Math.floor(W / pitch);
      const frac = live ? Math.min(1, (now - last) / WAVE_STEP_MS) : 0;
      rest(Math.max(0, 1 - Math.max(0, ...h.slice(-n)) / 0.12));
      for (let i = 0; i < n + 1 && i < h.length; i++) {
        const bh = h[h.length - 1 - i] * c.height;
        const x = W - (i + 1 + frac) * pitch + 2 * dpr;
        if (bh >= 2 * dpr && x >= 0) bar(x, bw, bh);
      }
    };
    const fit = () => {
      c.width = Math.round(c.clientWidth * dpr);
      c.height = Math.round(c.clientHeight * dpr);
      draw(performance.now());
    };
    const sized = new ResizeObserver(fit);
    sized.observe(c);
    fit();
    const frame = (now: number) => {
      const target = voiceLevel();
      env.current += (target - env.current) * (target > env.current ? 0.5 : 0.15);
      while (now - last >= WAVE_STEP_MS) {
        last += WAVE_STEP_MS;
        hist.current.push(Math.min(1, env.current * (0.55 + 0.45 * Math.random())));
        if (hist.current.length > WAVE_KEEP) hist.current.shift();
      }
      draw(now);
      raf = requestAnimationFrame(frame);
    };
    if (live) raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      sized.disconnect();
    };
  }, [live, writing, still]);
  return <canvas className="voice-wave" ref={ref} aria-hidden="true" />;
}

function Elapsed({
  since,
  running,
  capped,
}: {
  since: number;
  running: boolean;
  capped: boolean;
}) {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(performance.now()), 250);
    return () => clearInterval(t);
  }, [running]);
  const s = capped
    ? MAX_SECONDS
    : since
      ? Math.max(0, Math.min(MAX_SECONDS, Math.floor((now - since) / 1000)))
      : 0;
  return (
    <span className="voice-time" data-capped={capped ? "" : undefined} aria-hidden="true">
      <span data-testid="voice-time">
        {Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}
      </span>
    </span>
  );
}

/** One word on screen: its key stays with it for as long as it's there, so it never re-enters. */
interface Word {
  id: number;
  text: string;
  /** What the recognizer said here before, when it changed its mind. */
  was?: string;
}

/**
 * The transcript as words that keep their keys: a word already shown stays the same element
 * (nothing re-renders it), a new one is appended, and one the recognizer revised is a new
 * element that cross-fades from the old.
 */
function useWords(text: string): Word[] {
  const ref = useRef<{ text: string; words: Word[]; next: number }>({ text: "", words: [], next: 1 });
  const r = ref.current;
  if (r.text !== text) {
    const prev = r.words;
    r.words = text
      .split(/\s+/)
      .filter(Boolean)
      .map((t, i) => (prev[i]?.text === t ? prev[i] : { id: r.next++, text: t, was: prev[i]?.text }));
    r.text = text;
  }
  return r.words;
}

const LINE = 20; // the transcript's line height (--lh-20)
const MAX_LINES = 3;

/**
 * The words so far, the pill's full width, three lines at most: past three the block moves up, the
 * oldest line leaving under a soft edge. Before the first word it's no height at all. The height and
 * the move are set here and eased in CSS (200ms), so a size change is never a snap.
 */
function Transcript({ text, final }: { text: string; final: boolean }) {
  const words = useWords(text);
  const clip = useRef<HTMLDivElement>(null);
  const flow = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const c = clip.current;
    const f = flow.current;
    if (!c || !f) return;
    const lines = words.length ? Math.max(1, Math.round(f.offsetHeight / LINE)) : 0;
    c.style.height = `${Math.min(lines, MAX_LINES) * LINE}px`;
    f.style.transform = `translateY(${-Math.max(0, lines - MAX_LINES) * LINE}px)`;
    c.dataset.scrolled = lines > MAX_LINES ? "1" : "0";
    // from the second measurement on, changes ease
    const t = requestAnimationFrame(() => (c.dataset.ready = "1"));
    return () => cancelAnimationFrame(t);
  }, [text, words]);
  return (
    <div className="voice-clip" ref={clip}>
      <div className="voice-flow" ref={flow} data-testid="voice-text" data-final={final ? "1" : "0"} aria-live="polite">
        {words.map((w, i) => (
          <Fragment key={w.id}>
            {i > 0 && " "}
            <span className={`voice-w ${w.was ? "is-revised" : "is-new"}`} data-was={w.was}>
              {w.text}
            </span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * Hands-free's Cancel (ghost, its esc cap) and Insert (ink, its ↵). As `room`, an unseen copy that
 * keeps the zone as wide as they are in every phase, so the zone's edge never moves.
 */
function FreeActions({ room, keys, ready, freeKey }: { room?: boolean; keys: boolean; ready?: boolean; freeKey?: string }) {
  const hold = (e: RPointerEvent) => e.preventDefault(); // focus stays on the terminal
  const cancel = (
    <>
      <span className="voice-lbl-c">Cancel</span>
      {keys && <span className="voice-key">esc</span>}
    </>
  );
  const insert = <>Insert{keys && <span className="voice-key">↵</span>}</>;
  const quiet = `voice-cta is-quiet ${keys ? "has-key" : "is-solo"}`;
  const primary = `voice-cta is-primary ${keys ? "" : "is-solo"}`;
  if (room)
    return (
      <span className="voice-z voice-z-room" aria-hidden="true">
        <span className={quiet}>{cancel}</span>
        <span className={primary}>{insert}</span>
      </span>
    );
  return (
    <span className="voice-z">
      <button type="button" className={quiet} data-testid="voice-cancel" aria-label="Cancel" title="Cancel (Esc)" onPointerDown={hold} onClick={cancelVoice}>
        {cancel}
      </button>
      <button
        type="button"
        className={primary}
        data-testid="voice-insert"
        aria-label="Insert"
        title={`Insert (Return or ${freeKey})`}
        disabled={!ready}
        onPointerDown={hold}
        onClick={() => void finishVoice()}
      >
        {insert}
      </button>
    </span>
  );
}

const LEAVE_MS = 160;

/**
 * The pill over session `sid`'s terminal while someone talks to it: centred under the pane's
 * head, a mark, the level, the time and the live words (three lines at most); hands-free adds
 * Stop and ✕. A notice (an error, or how to use the key) takes its place, a click or ✕ away.
 */
export function VoiceCard({ sid }: { sid: number }) {
  const v = useVoice();
  const mine = v.sid === sid && v.phase !== "idle";
  const reduced = useMedia(REDUCED_MQ);
  const keys = useMedia("(hover: hover) and (pointer: fine)"); // a keyboard's keys are worth showing
  const freeKey = useSyncExternalStore(onVoiceShortcuts, voiceFreeKeyLabel);
  // The pill's last state stays a moment after it ends, for it to fade out.
  const last = useRef(v);
  if (mine) last.current = v;
  const [leaving, setLeaving] = useState(false);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    setLeaving(!mine && last.current.phase !== "idle");
    if (mine) return;
    const t = setTimeout(
      () => {
        last.current = voiceState();
        setLeaving(false);
      },
      reduced ? 0 : LEAVE_MS,
    );
    return () => clearTimeout(t);
  }, [mine, reduced]);
  // Shown from the frame after it mounts (CSS eases it in), hidden again the moment it ends.
  const s = mine ? v : last.current;
  const here = s.phase !== "idle" && s.sid === sid;
  useEffect(() => {
    if (!mine) return setEntered(false);
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, [mine, here]);
  if (!here) return null;

  const notice = s.phase === "error" || s.phase === "hint";
  const live = s.phase === "starting" || s.phase === "recording";
  const running = s.phase === "recording" && !s.capped && !leaving;
  const finishing = s.phase === "finishing";
  const free = s.handsFree && live;
  const words = !notice && s.text.trim() !== "";
  const noticeText = s.message ?? "Hold to talk: keep holding while you speak.";
  // the zone: what you can do now, on the right
  const zone = notice ? (s.phase === "error" ? "error" : null) : finishing ? "write" : free ? "free" : "hold";
  const holdHint = s.cancelling ? "Release to cancel" : s.capped ? `${MAX_SECONDS / 60}-minute limit` : "Release to insert";
  // What a screen reader hears: the phase (the words are their own live region).
  const spoken = finishing
    ? "Inserting"
    : s.handsFree
      ? "Listening, hands-free. Press Return to insert, Esc to cancel."
      : "Listening. Let go to insert, Esc to cancel.";

  return (
    <div
      className={`voice-pill ${leaving ? "is-leaving" : ""}`}
      role="group"
      aria-label="Voice input"
      data-testid="voice-card"
      data-shown={entered && !leaving ? "" : undefined}
      data-words={words ? "" : undefined}
      data-phase={s.phase}
      data-hands-free={s.handsFree ? "1" : "0"}
      data-capped={s.capped ? "1" : "0"}
      data-cancelling={s.cancelling ? "1" : "0"}
      onClick={notice ? cancelVoice : undefined}
    >
      {!notice && (
        <span className="ds-sr" role="status">
          {spoken}
        </span>
      )}
      <div className="voice-row">
        <span className="voice-status">
          {notice ? (
            <span className={`voice-notice ${s.phase === "error" ? "is-error" : ""}`} title={noticeText}>
              {s.phase === "error" ? <Ic icon={IconMicrophoneOff} size={16} stroke={2} /> : <span className="voice-rec" />}
              <span className="voice-notice-text">{noticeText}</span>
            </span>
          ) : (
            <>
              {/* red while the microphone is open, stone once it isn't */}
              <span className="voice-rec" />
              <Wave live={live && !s.capped && !leaving} writing={finishing} still={reduced} />
              <Elapsed since={s.since} running={running} capped={s.capped} />
            </>
          )}
        </span>
        {zone && (
          <>
            <span className="voice-zone">
              {!notice && <FreeActions room keys={keys} />}
              {zone === "hold" && (
                <span key="hold" className={`voice-z voice-hint ${s.cancelling ? "is-cancelling" : s.capped ? "is-capped" : ""}`}>
                  {holdHint}
                </span>
              )}
              {zone === "free" && <FreeActions key="free" keys={keys} ready={s.phase === "recording"} freeKey={freeKey} />}
              {zone === "write" && (
                <span key="write" className="voice-z voice-shimmer">
                  Writing<span className="voice-lbl-c"> it down</span>…
                </span>
              )}
              {zone === "error" && (
                <span key="error" className="voice-z">
                  <button
                    type="button"
                    className="voice-cta is-quiet is-solo"
                    data-testid="voice-dismiss"
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelVoice();
                    }}
                  >
                    Dismiss
                  </button>
                </span>
              )}
            </span>
          </>
        )}
      </div>
      {!notice && (
        <div className="voice-words">
          <Transcript text={s.text} final={finishing} />
        </div>
      )}
    </div>
  );
}
